/*
  Equivalencia de talles
  ------------------------------------------------------------
  La caja de abajo de los chips muestra el desglose del talle: US, EUR, medida
  del pie. Hay dos fuentes, en este orden:

  1. `data-size-map` — el metafield `custom.size_equivalence` del producto, si
     está cargado. Manda, porque es una decisión explícita sobre ese producto.
  2. La tabla de `sections/size-guide.liquid`, que es de donde la toma la tienda
     en producción. La tabla se elige por tag de producto, así que el dato se
     carga una vez por tipo y no producto por producto.

  Se rinde al pasar por encima de un talle y al elegirlo; al salir vuelve al
  talle elegido, o se esconde si no hay ninguno.
*/
(() => {
  const roots = document.querySelectorAll('[data-size-equivalence]');
  if (!roots.length) return;

  const norm = (value) => String(value == null ? '' : value).trim();

  /*
    Las tablas de la guía traen la primera columna con el talle y las demás con
    sus equivalencias. `column-title` es el nombre de la columna y los
    `column-value` van en el mismo orden en todas, así que la fila `i` de la
    primera columna se corresponde con la fila `i` del resto.

    Si el producto no tiene tabla —ninguna matchea sus tags— esto devuelve un
    mapa vacío y la caja simplemente no se muestra.
  */
  let tableMap = null;
  const buildTableMap = () => {
    if (tableMap) return tableMap;
    tableMap = {};

    document.querySelectorAll('.size-guide__table').forEach((table) => {
      const columns = Array.prototype.slice.call(table.querySelectorAll('.column'));
      if (columns.length < 2) return;

      const cells = (column) =>
        Array.prototype.slice.call(column.querySelectorAll('.column-value'));
      const title = (column) => norm(column.querySelector('.column-title')?.textContent);

      cells(columns[0]).forEach((cell, row) => {
        const key = norm(cell.textContent);
        if (key === '' || tableMap[key]) return;

        const pairs = [];
        for (let i = 1; i < columns.length; i += 1) {
          const value = norm(cells(columns[i])[row]?.textContent);
          if (value !== '') pairs.push([title(columns[i]), value]);
        }
        if (pairs.length) tableMap[key] = pairs;
      });
    });

    return tableMap;
  };

  roots.forEach((root) => {
    const fieldset = root.closest('fieldset');
    if (!fieldset) return;

    let ownMap = null;
    if (root.dataset.sizeMap) {
      try {
        const raw = JSON.parse(root.dataset.sizeMap);
        const labels = [['us', 'US'], ['eur', 'EUR'], ['cm', 'MEDIDA PIE (cm)']];
        ownMap = {};
        Object.keys(raw).forEach((key) => {
          const pairs = labels
            .map(([field, label]) => [label, norm(raw[key]?.[field])])
            .filter(([, value]) => value !== '');
          if (pairs.length) ownMap[key] = pairs;
        });
      } catch (e) {
        ownMap = null;
      }
    }

    const entryFor = (sizeKey) => {
      const key = norm(sizeKey);
      if (key === '') return null;
      if (ownMap && ownMap[key]) return ownMap[key];
      return buildTableMap()[key] || null;
    };

    /*
      Un talle sin ninguna medida no tiene nada que mostrar, y ahí la caja se
      esconde entera: si no, queda su borde de 0.5px con el interior en blanco.
    */
    const render = (sizeKey) => {
      const pairs = entryFor(sizeKey);
      root.textContent = '';

      if (!pairs) {
        root.hidden = true;
        return;
      }

      pairs.forEach(([label, value]) => {
        const span = document.createElement('span');
        span.textContent = label ? `${label}: ${value}` : value;
        root.appendChild(span);
      });
      root.hidden = false;
    };

    const selected = () =>
      fieldset.querySelector('input[type="radio"]:checked')?.value || '';

    const renderSelected = () => render(selected());

    fieldset.addEventListener('change', (e) => {
      if (!(e.target instanceof HTMLInputElement)) return;
      if (e.target.type !== 'radio') return;
      render(e.target.value);
    });

    /*
      Hover sobre el chip. El input es el que tiene el valor, pero el que recibe
      el mouse es su label, así que se escucha en el fieldset y se sube desde el
      target hasta el control.
    */
    fieldset.addEventListener('mouseover', (e) => {
      const label = e.target.closest?.('label');
      if (!label || !fieldset.contains(label)) return;

      const input = label.control
        || (label.htmlFor && document.getElementById(label.htmlFor))
        || label.querySelector('input[type="radio"]');
      if (!input || input.type !== 'radio') return;

      render(input.value);
    });

    fieldset.addEventListener('mouseleave', renderSelected);

    /*
      El JS es la autoridad sobre la caja: si hay un chip marcado se rinde desde
      el mapa, así Liquid y JS no pueden divergir. Sin nada marcado —el estado
      inicial cuando el talle es obligatorio— la caja queda escondida.
    */
    renderSelected();
  });
})();

/*
  Elegir talle es obligatorio
  ------------------------------------------------------------
  Madrid resuelve `product.selected_or_first_available_variant` y lo escribe en el
  input hidden del form, asi que "Anadir al carrito" mandaba al carrito la primera
  variante disponible aunque el visitante no hubiera tocado ningun talle: se
  agregaba "Negro / 39" sin que nadie lo eligiera.

  `sections/main-product.liquid` ya deja ese input **vacio** cuando el producto
  tiene una opcion que no es color con mas de un valor y la URL no trae `?variant=`.
  Este modulo pone la otra mitad: el estado del CTA y el corte del submit.

  El corte va en **captura sobre `document`**: `product-form.js` escucha el submit
  en burbuja sobre el propio form, asi que un listener de captura en un ancestro
  corre antes y `stopPropagation()` evita que llegue. Registrarlo en el form no
  alcanzaria — haria falta `stopImmediatePropagation()` y ganar la carrera de
  registro, que no esta garantizada.

  El chequeo de "falta elegir" se hace **contra el DOM** (un grupo de radios sin
  `:checked`, un select vacio) y no contra el Liquid: asi cubre los tres pickers
  del tema (botones, pills, dropdown) sin repetir la heuristica de cual opcion es
  la de color.
*/
(() => {
  if (!document.querySelector('.pdp--ig')) return;

  const STRINGS = window.variantStrings || {};
  const ERROR_TEXT = STRINGS.selectSizeError || 'Elegí un talle para continuar';
  const CTA_TEXT = STRINGS.selectSizeCta || 'Elegí un talle';
  const ADD_TEXT = STRINGS.addToCart || 'Añadir al carrito';

  const isAddForm = (form) =>
    form instanceof HTMLFormElement && form.getAttribute('data-type') === 'add-to-cart-form';

  /* Un grupo cuenta como "sin elegir" si tiene controles y ninguno esta activo.
     Los radios deshabilitados (talle sin stock) no cuentan como eleccion. */
  const missingGroups = (picker) => {
    if (!picker) return [];
    /* `product-form__controls` es el fieldset por opcion del picker de botones y
       de pills; `product-form__input` es el wrapper del dropdown. Se piden los dos
       para no atarse a un picker. */
    const groups = [...picker.querySelectorAll('.product-form__controls, .product-form__input')];
    return groups.filter((group) => {
      const radios = group.querySelectorAll('input[type="radio"]');
      if (radios.length) return !group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      if (select) return !select.value;
      return false;
    });
  };

  const pickerFor = (form) => {
    const section = form.closest('.shopify-section') || document;
    return section.querySelector('variant-radios, variant-selects');
  };

  const errorEls = (form) => {
    const host = form.closest('product-form') || form.parentElement;
    const wrapper = host && host.querySelector('.product-form__error-message-wrapper');
    return { wrapper, text: wrapper && wrapper.querySelector('.product-form__error-message') };
  };

  const showError = (form, on) => {
    const { wrapper, text } = errorEls(form);
    if (!wrapper) return;
    if (on) {
      if (text) text.textContent = ERROR_TEXT;
      wrapper.removeAttribute('hidden');
      wrapper.setAttribute('role', 'alert');
    } else {
      wrapper.setAttribute('hidden', '');
      wrapper.removeAttribute('role');
    }
  };

  /* El CTA se deja **habilitado** a proposito: deshabilitado no dispara click y el
     visitante se queda sin saber por que no pasa nada. El aviso lo da el submit. */
  const paint = (form) => {
    const missing = missingGroups(pickerFor(form));
    const needs = missing.length > 0;
    form.toggleAttribute('data-needs-selection', needs);

    const addButton = form.querySelector('[name="add"]');
    const addText = addButton && addButton.querySelector('span');
    if (addButton && addText) {
      if (needs) {
        addButton.removeAttribute('disabled');
        addText.textContent = CTA_TEXT;
      } else if (addText.textContent === CTA_TEXT) {
        addText.textContent = ADD_TEXT;
      }
    }

    /* El boton de pago rapido saltea el form: si no hay variante, tiene que
       quedar fuera de alcance o manda a checkout con el id vacio. */
    const checkout = form.querySelector('.product-form__checkout');
    if (checkout) checkout.toggleAttribute('data-needs-selection', needs);

    if (!needs) showError(form, false);
    return missing;
  };

  const forms = [...document.querySelectorAll('form[data-type="add-to-cart-form"]')];
  if (!forms.length) return;

  forms.forEach((form) => {
    paint(form);
    const picker = pickerFor(form);
    if (picker) picker.addEventListener('change', () => paint(form));
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isAddForm(form)) return;

      const missing = paint(form);
      const id = form.querySelector('[name="id"]');
      if (!missing.length && id && id.value) return;

      event.preventDefault();
      event.stopPropagation();
      showError(form, true);

      const target = missing[0] || form;
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusable = target.querySelector('input:not([disabled]), select, label');
      if (focusable && focusable.focus) focusable.focus({ preventScroll: true });
    },
    true
  );

  /* El sticky y el picker principal comparten estado por `variant:change`; ese
     evento solo se dispara cuando hay variante, asi que repintar ahi alcanza para
     limpiar el aviso despues de elegir. */
  document.addEventListener('variant:change', () => forms.forEach(paint));
})();

/*
  Red de contencion: recalcular la galeria al rotar
  ------------------------------------------------------------
  El reporte decia que el slide conservaba su `width` inline al cambiar el
  viewport y la foto quedaba cortada. **No se reprodujo**: medido con Chrome
  headless sobre esta misma galeria, pasar de 428 a 375 sin disparar ningun
  evento sintetico (o sea, solo cambiando la metrica del dispositivo) deja el
  slide en `width: 375px`. Swiper v8 trae `resizeObserver` prendido por defecto y
  ese es el que lo resuelve.

  Igual queda esta red, que es barata: `update()` es idempotente y solo corre en
  `orientationchange`, un evento que en un telefono se dispara una vez por giro.
  Cubre el caso de que el ResizeObserver no llegue a tiempo mientras el navegador
  reacomoda la barra de direcciones — que es el escenario donde el bug seria real.
*/
(() => {
  if (!document.querySelector('.pdp--ig')) return;

  const update = () => {
    document.querySelectorAll('.product__media-list').forEach((el) => {
      if (!el.swiper) return;
      /* Dos pasadas: la primera con el layout viejo todavia asentandose. */
      el.swiper.update();
      requestAnimationFrame(() => el.swiper && el.swiper.update());
    });
  };

  window.addEventListener('orientationchange', () => setTimeout(update, 120));
})();

/*
  El rotulo del grupo tiene que decir la opcion elegida
  ------------------------------------------------------------
  `product-variant-picker.liquid` imprime dos spans por opcion:

      <span class="product-options-name-inactive">Número</span>
      <span class="product-options-name-active" id="OptionName-<seccion>-<pos>">39</span>

  El CSS alterna cual se ve segun haya o no algo elegido, pero **el texto del span
  activo nunca se actualiza**: sale del `option.selected_value` del render inicial
  y ahi se queda. Al elegir 43 el rotulo seguia diciendo 39.

  No es un bug de esta capa, es una laguna de Madrid: `VariantSelects.infoBlocksList`
  (assets/global.js) lista `ColorName` pero no `OptionName`, asi que
  `updateProductInfoBlocks` refresca el rotulo del color y ninguno mas. Y no se
  arregla agregandolo a esa lista: los ids no tienen la misma forma —el del color
  es `ColorName-<seccion>` y el de las otras opciones lleva ademas la posicion—,
  asi que el lookup por nombre no lo encontraria.

  Se resuelve aca en vez de tocar `global.js` para no editar la base del tema.
*/
(() => {
  if (!document.querySelector('.pdp--ig')) return;

  const syncOptionNames = (picker) => {
    picker.querySelectorAll('.product-form__controls').forEach((group) => {
      const active = group.querySelector('.product-options-name-active');
      if (!active) return;

      /* El del color lo mantiene Madrid; tocarlo seria pelearse con
         `updateProductInfoBlocks` por el mismo nodo. */
      if (active.id.indexOf('ColorName-') === 0) return;

      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        active.textContent = checked.value;
        return;
      }

      const select = group.querySelector('select');
      if (select && select.value) active.textContent = select.value;
    });
  };

  document.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
    syncOptionNames(picker);
    picker.addEventListener('change', () => syncOptionNames(picker));
  });

  /* Despues de un `renderProductInfo` el picker puede haberse reemplazado, asi
     que se vuelve a sincronizar sobre el que este en el DOM en ese momento. */
  document.addEventListener('variant:change', () => {
    document.querySelectorAll('variant-radios, variant-selects').forEach(syncOptionNames);
  });
})();
